import { useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectTherapistName,
} from "../../store/slices/authSlice";
import { useGetStorageUsageQuery } from "../../store/services/patientFilesApi";
import {
  MAX_FILE_SIZE_MB,
  getFileTypeCategory,
  isFilePreviewable,
} from "../../firebase";
import {
  Folder,
  FolderOpen,
  File,
  FilePdf,
  FileDoc,
  FileXls,
  Image,
  UploadSimple,
  Trash,
  PencilSimple,
  X,
  Check,
  Eye,
  CaretLeft,
  CloudArrowUp,
  FolderPlus,
  Warning,
  Info,
  Camera,
  ImageSquare,
} from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import EmptyDraw from "../ui/EmptyDraw";
import "./PatientFilesTab.css";

// File type icons (same as PatientFilesTab)
const FILE_ICONS = {
  image: Image,
  pdf: FilePdf,
  word: FileDoc,
  excel: FileXls,
  other: File,
};

// Folder colors (same as PatientFilesTab)
const FOLDER_COLORS = [
  "#6366f1", // Indigo
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#ef4444", // Red
  "#f97316", // Orange
  "#eab308", // Yellow
  "#22c55e", // Green
  "#14b8a6", // Teal
  "#3b82f6", // Blue
  "#6b7280", // Gray
];

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

// Generate unique IDs for local staging
let nextId = 1;
const generateId = () => `staging_${nextId++}`;

/**
 * PatientFilesStagingTab - Same UI as PatientFilesTab but stores everything
 * in local state. Files are kept as { id, file (File object), folderId, description }
 * and folders as { id, name, color }. These are passed up to the parent via
 * onStagedDataChange so the parent can upload them after creating the patient.
 */
function PatientFilesStagingTab({ onStagedDataChange }) {
  const clinicId = useSelector(selectClinicId);
  const therapistName = useSelector(selectTherapistName);

  // Local staged data
  const [stagedFiles, setStagedFiles] = useState([]);
  const [stagedFolders, setStagedFolders] = useState([]);

  // UI state (same as PatientFilesTab)
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [showFilePreview, setShowFilePreview] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [folderForm, setFolderForm] = useState({ name: "", color: FOLDER_COLORS[0] });
  const [fileDescriptionForm, setFileDescriptionForm] = useState("");

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Check if mobile device
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Storage usage (read-only, just for display)
  const { data: storageUsage } = useGetStorageUsageQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Notify parent whenever staged data changes
  const notifyParent = (files, folders) => {
    if (onStagedDataChange) {
      onStagedDataChange({ files, folders });
    }
  };

  // Filter files by selected folder
  const filteredFiles = selectedFolder
    ? stagedFiles.filter((f) => f.folderId === selectedFolder)
    : stagedFiles;

  // Handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = Array.from(e.dataTransfer.files);
      handleFilesAdd(droppedFiles);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFolder, stagedFiles, stagedFolders]
  );

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFilesAdd(selectedFiles);
    // Reset all inputs
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };

  const handleFilesAdd = (filesToAdd) => {
    setUploadError(null);

    const newFiles = [];
    for (const file of filesToAdd) {
      // Check file size
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setUploadError(`הקובץ ${file.name} גדול מדי. הגודל המקסימלי הוא ${MAX_FILE_SIZE_MB}MB`);
        continue;
      }

      // Create a local preview URL for images
      const previewUrl = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null;

      newFiles.push({
        id: generateId(),
        file, // The actual File object
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        folderId: selectedFolder,
        description: null,
        previewUrl,
        uploadedByName: therapistName,
        createdAt: new Date().toISOString(),
      });
    }

    if (newFiles.length > 0) {
      const updatedFiles = [...stagedFiles, ...newFiles];
      setStagedFiles(updatedFiles);
      notifyParent(updatedFiles, stagedFolders);
    }
  };

  const handleCreateFolder = () => {
    if (!folderForm.name.trim()) return;

    const newFolder = {
      id: generateId(),
      name: folderForm.name.trim(),
      color: folderForm.color,
    };

    const updatedFolders = [...stagedFolders, newFolder];
    setStagedFolders(updatedFolders);
    notifyParent(stagedFiles, updatedFolders);

    setShowFolderModal(false);
    setFolderForm({ name: "", color: FOLDER_COLORS[0] });
  };

  const handleUpdateFolder = () => {
    if (!editingFolder || !folderForm.name.trim()) return;

    const updatedFolders = stagedFolders.map((f) =>
      f.id === editingFolder.id
        ? { ...f, name: folderForm.name.trim(), color: folderForm.color }
        : f
    );
    setStagedFolders(updatedFolders);
    notifyParent(stagedFiles, updatedFolders);

    setEditingFolder(null);
    setFolderForm({ name: "", color: FOLDER_COLORS[0] });
  };

  const handleDeleteFolder = (folderId) => {
    // Move files from this folder to root
    const updatedFiles = stagedFiles.map((f) =>
      f.folderId === folderId ? { ...f, folderId: null } : f
    );
    const updatedFolders = stagedFolders.filter((f) => f.id !== folderId);

    setStagedFiles(updatedFiles);
    setStagedFolders(updatedFolders);
    notifyParent(updatedFiles, updatedFolders);

    setShowDeleteConfirm(null);
    if (selectedFolder === folderId) {
      setSelectedFolder(null);
    }
  };

  const handleUpdateFileDescription = () => {
    if (!editingFile) return;

    const updatedFiles = stagedFiles.map((f) =>
      f.id === editingFile.id
        ? { ...f, description: fileDescriptionForm.trim() || null }
        : f
    );
    setStagedFiles(updatedFiles);
    notifyParent(updatedFiles, stagedFolders);

    setEditingFile(null);
    setFileDescriptionForm("");
  };

  const handleMoveFileToFolder = (fileId, folderId) => {
    const updatedFiles = stagedFiles.map((f) =>
      f.id === fileId ? { ...f, folderId: folderId || null } : f
    );
    setStagedFiles(updatedFiles);
    notifyParent(updatedFiles, stagedFolders);
  };

  const handleDeleteFile = (fileId) => {
    const fileToDelete = stagedFiles.find((f) => f.id === fileId);
    // Revoke object URL to prevent memory leaks
    if (fileToDelete?.previewUrl) {
      URL.revokeObjectURL(fileToDelete.previewUrl);
    }

    const updatedFiles = stagedFiles.filter((f) => f.id !== fileId);
    setStagedFiles(updatedFiles);
    notifyParent(updatedFiles, stagedFolders);
    setShowDeleteConfirm(null);
  };

  const handlePreview = (file) => {
    if (file.previewUrl || file.fileType === "application/pdf") {
      setShowFilePreview(file);
    }
  };

  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setFolderForm({ name: folder.name, color: folder.color || FOLDER_COLORS[0] });
  };

  const openEditFile = (file) => {
    setEditingFile(file);
    setFileDescriptionForm(file.description || "");
  };

  const FileIcon = ({ fileType, size = 24 }) => {
    const category = getFileTypeCategory(fileType);
    const Icon = FILE_ICONS[category] || File;
    return <Icon size={size} weight="duotone" />;
  };

  // Render storage usage bar
  const renderStorageUsage = () => {
    if (!storageUsage) return null;

    const { usedStorageInMb, allowedStorageInMbMaxPerClinic, percentUsed } = storageUsage;
    const isNearLimit = percentUsed > 80;

    return (
      <div className={`storage-usage-bar ${isNearLimit ? "warning" : ""}`}>
        <div className="storage-info">
          <span className="storage-label">שימוש באחסון</span>
          <span className="storage-values">
            {usedStorageInMb.toFixed(1)} / {allowedStorageInMbMaxPerClinic} MB
          </span>
        </div>
        <div className="storage-progress">
          <div
            className="storage-progress-fill"
            style={{ width: `${Math.min(100, percentUsed)}%` }}
          />
        </div>
        {isNearLimit && (
          <div className="storage-warning">
            <Warning size={14} weight="bold" />
            מתקרב למגבלת האחסון
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="patient-files-tab">
      {/* Header */}
      <div className="files-header">
        <div className="files-header-title">
          <Folder size={22} weight="duotone" />
          <h3>תיק מטופל</h3>
          <span className="files-count">
            {stagedFiles.length} קבצים
          </span>
        </div>

        <div className="files-header-actions">
          <button
            className="files-btn secondary"
            onClick={() => setShowFolderModal(true)}
          >
            <FolderPlus size={18} weight="bold" />
            תיקייה חדשה
          </button>

          {/* Camera Button - Mobile only */}
          {isMobile && (
            <button
              className="files-btn primary camera-btn"
              onClick={() => cameraInputRef.current?.click()}
            >
              <Camera size={18} weight="bold" />
              צלם
            </button>
          )}

          {/* Gallery Button - Mobile only */}
          {isMobile && (
            <button
              className="files-btn primary gallery-btn"
              onClick={() => galleryInputRef.current?.click()}
            >
              <ImageSquare size={18} weight="bold" />
              גלריה
            </button>
          )}

          {/* Regular File Upload */}
          <button
            className="files-btn primary"
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadSimple size={18} weight="bold" />
            {isMobile ? "קובץ" : "העלאת קובץ"}
          </button>

          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>
      </div>

      {/* Storage Usage */}
      {renderStorageUsage()}

      {/* Staging info banner */}
      {stagedFiles.length > 0 && (
        <div className="staging-info-banner">
          <Info size={16} weight="bold" />
          <span>הקבצים יישמרו יחד עם שמירת המטופל</span>
        </div>
      )}

      {/* Error Message */}
      {uploadError && (
        <div className="files-error">
          <Warning size={18} weight="bold" />
          <span>{uploadError}</span>
          <button onClick={() => setUploadError(null)}>
            <X size={16} weight="bold" />
          </button>
        </div>
      )}

      {/* Folders Section */}
      {stagedFolders.length > 0 && (
        <div className="folders-section">
          <div className="folders-header">
            <span>תיקיות</span>
          </div>
          <div className="folders-list">
            {/* Root folder option */}
            <button
              className={`folder-item ${selectedFolder === null ? "active" : ""}`}
              onClick={() => setSelectedFolder(null)}
            >
              <FolderOpen size={20} weight="duotone" />
              <span>כל הקבצים</span>
            </button>

            {stagedFolders.map((folder) => (
              <div
                key={folder.id}
                className={`folder-item ${selectedFolder === folder.id ? "active" : ""}`}
              >
                <button
                  className="folder-btn"
                  onClick={() => setSelectedFolder(folder.id)}
                >
                  <Folder
                    size={20}
                    weight="duotone"
                    style={{ color: folder.color || FOLDER_COLORS[0] }}
                  />
                  <span>{folder.name}</span>
                </button>
                <div className="folder-actions">
                  <button
                    className="folder-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditFolder(folder);
                    }}
                  >
                    <PencilSimple size={14} weight="bold" />
                  </button>
                  <button
                    className="folder-action-btn delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm({ type: "folder", item: folder });
                    }}
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Breadcrumb when in folder */}
      {selectedFolder && (
        <div className="files-breadcrumb">
          <button onClick={() => setSelectedFolder(null)}>
            <CaretLeft size={14} weight="bold" />
            חזרה לכל הקבצים
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">
            {stagedFolders.find((f) => f.id === selectedFolder)?.name || "תיקייה"}
          </span>
        </div>
      )}

      {/* Drop Zone */}
      <div
        className={`files-drop-zone ${isDragging ? "dragging" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {filteredFiles.length === 0 && !isDragging ? (
          <div className="drop-zone-empty">
            <EmptyDraw variant="files" />
            <h4>אין קבצים עדיין</h4>
            <p>גרור קבצים לכאן או לחץ על "העלאת קובץ"</p>
            <span className="drop-zone-hint">
              <Info size={14} />
              גודל מקסימלי: {MAX_FILE_SIZE_MB}MB לקובץ
            </span>
          </div>
        ) : isDragging ? (
          <div className="drop-zone-dragging">
            <CloudArrowUp size={48} weight="duotone" />
            <h4>שחרר כדי להוסיף</h4>
          </div>
        ) : (
          <div className="files-grid">
            {filteredFiles.map((file) => (
              <div key={file.id} className="file-card">
                <div className="file-card-icon">
                  {file.previewUrl ? (
                    <img
                      src={file.previewUrl}
                      alt={file.fileName}
                      style={{
                        width: 48,
                        height: 48,
                        objectFit: "cover",
                        borderRadius: "var(--radius-md)",
                      }}
                    />
                  ) : (
                    <FileIcon fileType={file.fileType} size={32} />
                  )}
                </div>

                <div className="file-card-info">
                  <span className="file-name" title={file.fileName}>
                    {file.fileName}
                  </span>
                  {file.description && (
                    <span className="file-description">{file.description}</span>
                  )}
                  <div className="file-meta">
                    <span>{formatFileSize(file.fileSize)}</span>
                  </div>
                </div>

                <div className="file-card-actions">
                  {file.previewUrl && (
                    <button
                      className="file-action-btn"
                      onClick={() => handlePreview(file)}
                      title="תצוגה מקדימה"
                    >
                      <Eye size={16} weight="bold" />
                    </button>
                  )}
                  <button
                    className="file-action-btn"
                    onClick={() => openEditFile(file)}
                    title="עריכת תיאור"
                  >
                    <PencilSimple size={16} weight="bold" />
                  </button>
                  <button
                    className="file-action-btn delete"
                    onClick={() => setShowDeleteConfirm({ type: "file", item: file })}
                    title="מחיקה"
                  >
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Folder Modal */}
      {(showFolderModal || editingFolder) && (
        <div className="files-modal-overlay" onClick={() => {
          setShowFolderModal(false);
          setEditingFolder(null);
          setFolderForm({ name: "", color: FOLDER_COLORS[0] });
        }}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <div className="files-modal-header">
              <h3>{editingFolder ? "עריכת תיקייה" : "תיקייה חדשה"}</h3>
              <ModalCloseButton onClick={() => {
                  setShowFolderModal(false);
                  setEditingFolder(null);
                  setFolderForm({ name: "", color: FOLDER_COLORS[0] });
                }} />
            </div>

            <div className="files-modal-body">
              <div className="form-group">
                <label>שם התיקייה</label>
                <input
                  type="text"
                  value={folderForm.name}
                  onChange={(e) => setFolderForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="לדוגמה: אבחונים"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>צבע</label>
                <div className="color-picker">
                  {FOLDER_COLORS.map((color) => (
                    <button
                      key={color}
                      className={`color-option ${folderForm.color === color ? "active" : ""}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFolderForm((prev) => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="files-modal-footer">
              <button
                className="files-btn secondary"
                onClick={() => {
                  setShowFolderModal(false);
                  setEditingFolder(null);
                  setFolderForm({ name: "", color: FOLDER_COLORS[0] });
                }}
              >
                ביטול
              </button>
              <button
                className="files-btn primary"
                onClick={editingFolder ? handleUpdateFolder : handleCreateFolder}
                disabled={!folderForm.name.trim()}
              >
                <Check size={16} weight="bold" />
                {editingFolder ? "שמור" : "צור תיקייה"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit File Description Modal */}
      {editingFile && (
        <div className="files-modal-overlay" onClick={() => {
          setEditingFile(null);
          setFileDescriptionForm("");
        }}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <div className="files-modal-header">
              <h3>עריכת תיאור קובץ</h3>
              <ModalCloseButton onClick={() => {
                  setEditingFile(null);
                  setFileDescriptionForm("");
                }} />
            </div>

            <div className="files-modal-body">
              <div className="file-preview-info">
                <FileIcon fileType={editingFile.fileType} size={24} />
                <span>{editingFile.fileName}</span>
              </div>

              <div className="form-group">
                <label>תיאור (אופציונלי)</label>
                <textarea
                  value={fileDescriptionForm}
                  onChange={(e) => setFileDescriptionForm(e.target.value)}
                  placeholder="לדוגמה: אבחון מפסיכולוג מ-2024"
                  rows={3}
                />
              </div>

              {stagedFolders.length > 0 && (
                <div className="form-group">
                  <label>העבר לתיקייה</label>
                  <select
                    value={editingFile.folderId || ""}
                    onChange={(e) => {
                      handleMoveFileToFolder(editingFile.id, e.target.value || null);
                      setEditingFile((prev) => ({ ...prev, folderId: e.target.value || null }));
                    }}
                  >
                    <option value="">ללא תיקייה</option>
                    {stagedFolders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {folder.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="files-modal-footer">
              <button
                className="files-btn secondary"
                onClick={() => {
                  setEditingFile(null);
                  setFileDescriptionForm("");
                }}
              >
                ביטול
              </button>
              <button
                className="files-btn primary"
                onClick={handleUpdateFileDescription}
              >
                <Check size={16} weight="bold" />
                שמור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {showFilePreview && (
        <div className="files-modal-overlay preview-overlay" onClick={() => setShowFilePreview(null)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <span className="preview-filename">{showFilePreview.fileName}</span>
              <div className="preview-actions">
                <ModalCloseButton onClick={() => setShowFilePreview(null)} />
              </div>
            </div>
            <div className="preview-content">
              {showFilePreview.previewUrl ? (
                <img src={showFilePreview.previewUrl} alt={showFilePreview.fileName} />
              ) : showFilePreview.fileType === "application/pdf" ? (
                <div className="preview-not-available">
                  <File size={64} weight="duotone" />
                  <p>תצוגה מקדימה של PDF תהיה זמינה לאחר שמירת המטופל</p>
                </div>
              ) : (
                <div className="preview-not-available">
                  <File size={64} weight="duotone" />
                  <p>תצוגה מקדימה לא זמינה לסוג קובץ זה</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="files-modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="files-modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="files-modal-header">
              <h3>
                {showDeleteConfirm.type === "folder"
                  ? "מחיקת תיקייה"
                  : "מחיקת קובץ"}
              </h3>
            </div>

            <div className="files-modal-body">
              <div className="delete-warning">
                <Trash size={32} weight="duotone" />
                <p>
                  {showDeleteConfirm.type === "folder"
                    ? `האם אתה בטוח שברצונך למחוק את התיקייה "${showDeleteConfirm.item.name}"?`
                    : `האם אתה בטוח שברצונך למחוק את הקובץ "${showDeleteConfirm.item.fileName}"?`}
                </p>
              </div>
            </div>

            <div className="files-modal-footer">
              <button
                className="files-btn secondary"
                onClick={() => setShowDeleteConfirm(null)}
              >
                ביטול
              </button>
              <button
                className="files-btn danger"
                onClick={() => {
                  if (showDeleteConfirm.type === "folder") {
                    handleDeleteFolder(showDeleteConfirm.item.id);
                  } else {
                    handleDeleteFile(showDeleteConfirm.item.id);
                  }
                }}
              >
                <Trash size={16} weight="bold" />
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientFilesStagingTab;
